import { SelectHTMLAttributes, forwardRef, useId } from 'react'
import { cn } from '@utils/cn'
import { ChevronDown } from 'lucide-react'

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly label?: string
  readonly error?: string
}

export const Select = forwardRef<HTMLSelectElement, Readonly<SelectProps>>(
  ({ label, error, className, children, ...props }, ref) => {
    const generatedId = useId()
    const selectId = props.id ?? generatedId

    return (
      <div className="w-full min-w-0 max-w-full">
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-secondary-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative min-w-0 max-w-full">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              'w-full min-w-0 max-w-full appearance-none rounded-lg border bg-white px-4 py-2.5 pr-10 text-sm text-secondary-900',
              'transition-all duration-200',
              'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200',
              'disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed disabled:border-secondary-200',
              'hover:border-secondary-400',
              {
                'border-red-500 focus:ring-red-500': error,
                'border-secondary-300': !error,
              },
              className
            )}
            {...props}
          >
            {children}
          </select>
          <ChevronDown
            className={cn(
              'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 transition-colors',
              props.disabled && 'text-secondary-300'
            )}
          />
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-red-600">{error}</p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'
